<template>
  <PageLayout
    :is-loading="isLoading"
    page-title="Riwayat Campaign"
    :show-back-button="true"
    :show-banner="false"
    :show-sponsors="false"
    :hide-sidebar="true"
    @back="goBack"
  >
    <!-- User Profile Section -->
    <div class="profile-section">
      <div class="profile-card">
        <div class="profile-avatar">
          <img 
            :src="detailUser.image || require('@/assets/maternity.png')" 
            :alt="detailUser.name" 
            class="avatar-image" 
          />
        </div>
        <div class="profile-info">
          <div class="community-name">{{ detailUser.community ? detailUser.community.community : 'BERT Community' }}</div>
          <div class="user-name">{{ detailUser.name }}</div>
        </div>
        <div class="profile-stats">
          <div class="stat-item">
            <div class="stat-number">{{ campaignHistory.length }}</div>
            <div class="stat-label">Campaign</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Filter Section -->
    <div class="filter-section">
      <div class="filter-tabs">
        <button 
          class="filter-tab" 
          :class="{ 'active': activeFilter === 'all' }"
          @click="setFilter('all')"
        >
          Semua
        </button>
        <button 
          class="filter-tab" 
          :class="{ 'active': activeFilter === 'pending' }"
          @click="setFilter('pending')"
        >
          Pending
        </button>
        <button 
          class="filter-tab" 
          :class="{ 'active': activeFilter === 'approved' }"
          @click="setFilter('approved')"
        >
          Approved
        </button>
        <button 
          class="filter-tab" 
          :class="{ 'active': activeFilter === 'completed' }"
          @click="setFilter('completed')"
        >
          Selesai
        </button>
      </div>
    </div>

    <!-- Campaign History List -->
    <div class="history-section">
      <div v-if="filteredCampaigns.length === 0" class="empty-state">
        <div class="empty-icon">
          <i class="mdi mdi-clipboard-text-outline"></i>
        </div>
        <h3 class="empty-title">Belum Ada Campaign</h3>
        <p class="empty-description">Kamu belum mengikuti campaign apapun. Yuk mulai ikuti campaign yang menarik!</p>
        <button class="browse-button" @click="goToCampaigns">
          Jelajahi Campaign
        </button>
      </div>

      <div v-else class="campaign-list">
        <div v-for="(campaign, index) in filteredCampaigns" :key="index" class="campaign-history-item">
          <div class="campaign-image">
            <img 
              :src="campaign.campaign.logo_brand || require('@/assets/cta-background.png')" 
              :alt="campaign.campaign.name" 
              class="campaign-photo" 
            />
          </div>
          
          <div class="campaign-content">
            <div class="campaign-header">
              <h4 class="campaign-title">{{ campaign.campaign.name }}</h4>
              <div class="campaign-status" :class="getStatusClass(campaign.status)">
                <i :class="getStatusIcon(campaign.status)"></i>
                <span>{{ getStatusText(campaign.status) }}</span>
              </div>
            </div>
            
            <div class="campaign-meta">
              <div class="campaign-type">
                <i class="mdi mdi-tag"></i>
                {{ campaign.campaign.tipe_project }}
              </div>
              <div class="campaign-date">
                <i class="mdi mdi-calendar"></i>
                {{ formatDate(campaign.created_at) }}
              </div>
            </div>
            
            <div class="campaign-benefits">
              <div v-if="campaign.campaign.benefit_point" class="benefit-item">
                <i class="mdi mdi-star"></i>
                <span>{{ campaign.campaign.benefit_point }} Points</span>
              </div>
              <div v-if="campaign.campaign.benefit_fee" class="benefit-item">
                <i class="mdi mdi-cash"></i>
                <span>Fee</span>
              </div>
            </div>
          </div>
          
          <div class="campaign-actions">
            <button class="detail-button" @click="viewCampaignDetail(campaign.campaign_id)">
              Detail
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Campaign Statistics -->
    <div class="stats-section">
      <div class="stats-grid">
        <div class="stat-card">
          <div class="stat-icon pending">
            <i class="mdi mdi-clock-outline"></i>
          </div>
          <div class="stat-content">
            <div class="stat-number">{{ getStatusCount('PENDING') }}</div>
            <div class="stat-label">Pending</div>
          </div>
        </div>
        
        <div class="stat-card">
          <div class="stat-icon approved">
            <i class="mdi mdi-check-circle"></i>
          </div>
          <div class="stat-content">
            <div class="stat-number">{{ getStatusCount('APPROVED') }}</div>
            <div class="stat-label">Approved</div>
          </div>
        </div>
        
        <div class="stat-card">
          <div class="stat-icon completed">
            <i class="mdi mdi-trophy"></i>
          </div>
          <div class="stat-content">
            <div class="stat-number">{{ getStatusCount('BUKTI INSIGHT SUBMITTED') }}</div>
            <div class="stat-label">Selesai</div>
          </div>
        </div>
      </div>
    </div>
  </PageLayout>
</template>

<script>
import AppPageLayout from '@/components/ui/PageLayout.vue';

export default {
  name: "NewCampaignHistory",
  components: { 
    PageLayout: AppPageLayout
  },
  data() {
    return {
      isLoading: false,
      campaignHistory: [],
      detailUser: {},
      activeFilter: 'all'
    };
  },
  computed: {
    filteredCampaigns() {
      if (this.activeFilter === 'all') {
        return this.campaignHistory;
      } else if (this.activeFilter === 'pending') {
        return this.campaignHistory.filter(campaign => campaign.status === 'PENDING');
      } else if (this.activeFilter === 'approved') {
        return this.campaignHistory.filter(campaign => 
          campaign.status === 'APPROVED' || 
          campaign.status === 'STORYLINE SUBMITTED' ||
          campaign.status === 'DRAFT SUBMITTED' ||
          campaign.status === 'CONTENT APPROVED' ||
          campaign.status === 'BUKTI TAYANG SUBMITTED'
        );
      } else if (this.activeFilter === 'completed') {
        return this.campaignHistory.filter(campaign => 
          campaign.status === 'BUKTI INSIGHT SUBMITTED' ||
          campaign.status === 'REJECTED'
        );
      }
      return this.campaignHistory;
    }
  },
  methods: {
    loadCampaignHistory() {
      const baseURI = this.$settings.endPoint + "campaign-user/select";
      this.isLoading = true;
      
      return this.$http
        .get(baseURI, { 
          timeout: 10000,
          'headers': {
            'AppToken': this.$settings.jwtSecret,
          }
        })
        .then((response) => {
          this.isLoading = false;
          this.campaignHistory = response.data.datas || [];
        })
        .catch(error => {
          this.isLoading = false;
          console.log('Error:', error);
          this.handleError(error);
        });
    },
    
    setFilter(filter) {
      this.activeFilter = filter;
    },
    
    getStatusClass(status) {
      const statusClasses = {
        'PENDING': 'status-pending',
        'APPROVED': 'status-approved',
        'STORYLINE SUBMITTED': 'status-approved',
        'DRAFT SUBMITTED': 'status-approved',
        'CONTENT APPROVED': 'status-approved',
        'BUKTI TAYANG SUBMITTED': 'status-approved',
        'BUKTI INSIGHT SUBMITTED': 'status-completed',
        'REJECTED': 'status-rejected'
      };
      return statusClasses[status] || 'status-default';
    },
    
    getStatusIcon(status) {
      const statusIcons = {
        'PENDING': 'mdi mdi-clock-outline',
        'APPROVED': 'mdi mdi-check-circle',
        'STORYLINE SUBMITTED': 'mdi mdi-file-document',
        'DRAFT SUBMITTED': 'mdi mdi-file-document',
        'CONTENT APPROVED': 'mdi mdi-check-circle',
        'BUKTI TAYANG SUBMITTED': 'mdi mdi-upload',
        'BUKTI INSIGHT SUBMITTED': 'mdi mdi-trophy',
        'REJECTED': 'mdi mdi-close-circle'
      };
      return statusIcons[status] || 'mdi mdi-information';
    },
    
    getStatusText(status) {
      const statusTexts = {
        'PENDING': 'Pending',
        'APPROVED': 'Approved',
        'STORYLINE SUBMITTED': 'Storyline Submitted',
        'DRAFT SUBMITTED': 'Draft Submitted',
        'CONTENT APPROVED': 'Content Approved',
        'BUKTI TAYANG SUBMITTED': 'Bukti Tayang Submitted',
        'BUKTI INSIGHT SUBMITTED': 'Selesai',
        'REJECTED': 'Ditolak'
      };
      return statusTexts[status] || status;
    },
    
    getStatusCount(status) {
      if (status === 'BUKTI INSIGHT SUBMITTED') {
        return this.campaignHistory.filter(campaign => 
          campaign.status === 'BUKTI INSIGHT SUBMITTED'
        ).length;
      }
      return this.campaignHistory.filter(campaign => campaign.status === status).length;
    },
    
    viewCampaignDetail(campaignId) {
      this.$router.push(`/user/campaign-detail/${campaignId}`);
    },
    
    goToCampaigns() {
      this.$router.push('/user/campaign');
    },
    
    goBack() {
      this.$router.go(-1);
    },
    
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('id-ID', { 
        day: 'numeric', 
        month: 'short', 
        year: 'numeric' 
      });
    },
    
    fetchUserData() {
      const userDatas = this.$getUserInfo();
      this.detailUser = userDatas.sub;
    },
    
    handleError(error) {
      if (error.response) {
        if (error.response.status === 422) {
          console.error('Validation error:', error.response.data.errors);
        } else if (error.response.status === 500) {
          this.$router.push('/server-error');
        } else if (error.response.status === 401) {
          this.$router.push("/authorized-error");
        } else {
          this.$router.push('/page-not-found');
        }
      }
    },
  },
  mounted() {
    this.fetchUserData();
    this.loadCampaignHistory();
  }
};
</script>

<style scoped>
/* ========================================= */
/* PROFILE SECTION                           */
/* ========================================= */
.profile-section {
  padding: 24px 32px;
  margin: 0 auto 32px auto;
  background: white;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  max-width: 1200px;
  width: calc(100% - 64px);
}

.profile-card {
  display: flex;
  align-items: center;
  gap: 20px;
}

.profile-avatar {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
}

.avatar-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.profile-info {
  flex: 1;
}

.community-name {
  font-size: 0.9rem;
  color: #F2994A;
  font-weight: 600;
  margin-bottom: 4px;
}

.user-name {
  font-size: 1.3rem;
  font-weight: 700;
  color: #1F2937;
}

.profile-stats {
  display: flex;
  gap: 24px;
}

.stat-item {
  text-align: center;
}

.stat-number {
  font-size: 1.5rem;
  font-weight: 700;
  color: #4A90E2;
  line-height: 1;
}

.stat-label {
  font-size: 0.8rem;
  color: #6B7280;
  margin-top: 4px;
}

/* ========================================= */
/* FILTER SECTION                            */
/* ========================================= */
.filter-section {
  padding: 0 32px;
  margin: 0 auto 32px auto;
  max-width: 1200px;
  width: calc(100% - 64px);
}

.filter-tabs {
  display: flex;
  background: white;
  border-radius: 12px;
  padding: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.filter-tab {
  flex: 1;
  padding: 12px 16px;
  border: none;
  background: transparent;
  border-radius: 8px;
  font-weight: 600;
  color: #6B7280;
  cursor: pointer;
  transition: all 0.3s ease;
}

.filter-tab.active {
  background: #4A90E2;
  color: white;
}

.filter-tab:hover:not(.active) {
  background: #F3F4F6;
  color: #374151;
}

/* ========================================= */
/* HISTORY SECTION                           */
/* ========================================= */
.history-section {
  padding: 0 32px;
  margin: 0 auto 32px auto;
  max-width: 1200px;
  width: calc(100% - 64px);
}

.empty-state {
  background: white;
  border-radius: 16px;
  padding: 48px 24px;
  text-align: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.empty-icon {
  width: 80px;
  height: 80px;
  background: #F3F4F6;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px auto;
  font-size: 2.5rem;
  color: #9CA3AF;
}

.empty-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1F2937;
  margin: 0 0 12px 0;
}

.empty-description {
  color: #6B7280;
  margin: 0 0 32px 0;
  line-height: 1.6;
}

.browse-button {
  background: #4A90E2;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.browse-button:hover {
  background: #357ABD;
  transform: translateY(-2px);
}

.campaign-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.campaign-history-item {
  display: flex;
  align-items: center;
  padding: 20px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
}

.campaign-history-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.campaign-image {
  width: 80px;
  height: 80px;
  border-radius: 12px;
  overflow: hidden;
  margin-right: 20px;
  flex-shrink: 0;
}

.campaign-photo {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.campaign-content {
  flex: 1;
}

.campaign-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
}

.campaign-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #1F2937;
  margin: 0;
  line-height: 1.3;
}

.campaign-status {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  white-space: nowrap;
}

.status-pending {
  background: #FEF3C7;
  color: #D97706;
}

.status-approved {
  background: #D1FAE5;
  color: #059669;
}

.status-completed {
  background: #E0E7FF;
  color: #7C3AED;
}

.status-rejected {
  background: #FEE2E2;
  color: #DC2626;
}

.campaign-meta {
  display: flex;
  gap: 20px;
  margin-bottom: 12px;
}

.campaign-type,
.campaign-date {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.85rem;
  color: #6B7280;
}

.campaign-benefits {
  display: flex;
  gap: 12px;
}

.benefit-item {
  display: flex;
  align-items: center;
  gap: 4px;
  color: #4A90E2;
  font-size: 0.8rem;
  font-weight: 600;
}

.campaign-actions {
  margin-left: 16px;
}

.detail-button {
  background: #F2994A;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.detail-button:hover {
  background: #e0874a;
}

/* ========================================= */
/* STATS SECTION                             */
/* ========================================= */
.stats-section {
  padding: 0 32px;
  margin: 0 auto 32px auto;
  max-width: 1200px;
  width: calc(100% - 64px);
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
}

.stat-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: white;
}

.stat-icon.pending {
  background: linear-gradient(135deg, #F59E0B 0%, #F97316 100%);
}

.stat-icon.approved {
  background: linear-gradient(135deg, #10B981 0%, #059669 100%);
}

.stat-icon.completed {
  background: linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%);
}

.stat-content .stat-number {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1F2937;
  line-height: 1;
}

.stat-content .stat-label {
  font-size: 0.9rem;
  color: #6B7280;
  margin-top: 4px;
}

/* ========================================= */
/* MOBILE RESPONSIVE STYLES                  */
/* ========================================= */
@media (max-width: 768px) {
  .profile-section,
  .filter-section,
  .history-section,
  .stats-section {
    width: calc(100% - 40px);
    margin: 0 20px 24px 20px;
    padding: 16px;
  }
  
  .profile-card {
    flex-direction: column;
    text-align: center;
    gap: 16px;
  }
  
  .profile-avatar {
    width: 60px;
    height: 60px;
  }
  
  .user-name {
    font-size: 1.1rem;
  }
  
  .filter-tabs {
    flex-direction: column;
    gap: 4px;
  }
  
  .campaign-history-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
    padding: 16px;
  }
  
  .campaign-image {
    width: 100%;
    height: 120px;
    margin-right: 0;
  }
  
  .campaign-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
  
  .campaign-meta {
    flex-direction: column;
    gap: 8px;
  }
  
  .campaign-actions {
    margin-left: 0;
    width: 100%;
  }
  
  .detail-button {
    width: 100%;
  }
  
  .stats-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }
  
  .empty-state {
    padding: 32px 16px;
  }
  
  .empty-icon {
    width: 60px;
    height: 60px;
    font-size: 2rem;
  }
  
  .empty-title {
    font-size: 1.2rem;
  }
}
</style>